/**
 * The keyboard model.
 *
 * Gmail's vocabulary is the one users already have, and its real insight is the
 * three tiers: single-key actions, `g`-prefixed navigation, `*`-prefixed
 * selection. Copying the *structure* matters more than copying any binding,
 * because it is what keeps a large map learnable.
 */

export type Tier = 'action' | 'goto' | 'select'
export type Context = 'list' | 'thread' | 'compose' | 'global'

export interface Binding {
  readonly id: string
  readonly keys: string
  readonly tier: Tier
  readonly label: string
  /** Requires this capability; the binding is inert without it. */
  readonly capability?: string
  /** Where this binding is meaningful. */
  readonly contexts?: readonly Context[]
}

export const DEFAULT_BINDINGS: readonly Binding[] = [
  // Tier 1 - single key, acts on the selection if any, else the cursor row.
  { id: 'cursor.down', keys: 'j', tier: 'action', label: 'Next', contexts: ['list', 'thread'] },
  { id: 'cursor.up', keys: 'k', tier: 'action', label: 'Previous', contexts: ['list', 'thread'] },
  { id: 'open', keys: 'Enter', tier: 'action', label: 'Open', contexts: ['list'] },
  // `list` AS WELL AS `thread`, for the same reason `reply` below carries both
  // and with a worse consequence: `useKeyboard` runs the shell in the `list`
  // context and nothing else, so scoped to `thread` alone `u` could never fire.
  // Under 60rem the reading pane covers the list, and this is the key that
  // comes back from it — thread-only, there was no way out at all.
  { id: 'back', keys: 'u', tier: 'action', label: 'Back to list', contexts: ['list', 'thread'] },
  { id: 'select.toggle', keys: 'x', tier: 'action', label: 'Select', contexts: ['list'] },
  { id: 'archive', keys: 'e', tier: 'action', label: 'Archive', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'trash', keys: '#', tier: 'action', label: 'Delete', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'spam', keys: '!', tier: 'action', label: 'Report spam', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'star', keys: 's', tier: 'action', label: 'Star', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'read', keys: 'Shift+i', tier: 'action', label: 'Mark read', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'unread', keys: 'Shift+u', tier: 'action', label: 'Mark unread', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'snooze', keys: 'b', tier: 'action', label: 'Snooze', capability: 'mail.write', contexts: ['list', 'thread'] },
  /**
   * `l` AND `v` ARE BACK, and they are the two that needed the same thing.
   *
   * They were declared here once, resolved to a command the shell had no arm
   * for, fell through `runCommand`'s `default` and did nothing at all — so they
   * were DELETED, because a key that does nothing teaches the reader that the
   * client is broken. What they were waiting for is written into that deletion:
   * `Triage` had to grow `label(rows, add, remove)` and `moveTo(rows, mailbox)`
   * so the answer goes through the one mutation path that has an optimistic
   * patch and an undo, and there had to be somewhere to give the answer.
   * `Picker.tsx` is that somewhere and `useOps.ts` has both verbs; these two
   * keys open the question and the picker sends it.
   *
   * `m` (mute) IS BACK TOO, and it is the one that needed the other half.
   * `interaction.md` defines mute as `$muted` PLUS a generated rule that files
   * the thread's future replies, and the keyword alone would have told somebody
   * a conversation is muted while every reply to it kept arriving in the Inbox —
   * the one failure worse than no mute. `POST /mute` records the thread's root
   * Message-ID and INSTALLS the recompiled script before it answers, so a mute
   * that could not be installed is a mute that did not happen and says so.
   *
   * `.` (more actions) is gone for a different reason: nothing was designed
   * behind it here. The row cluster and the reading-pane toolbar are the
   * pointer surface, and `?` now lists every command there is — a third
   * vocabulary with no menu to open was a declaration and nothing else.
   */
  { id: 'label', keys: 'l', tier: 'action', label: 'Label…', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'move', keys: 'v', tier: 'action', label: 'Move to…', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'mute', keys: 'm', tier: 'action', label: 'Mute conversation', capability: 'mail.write', contexts: ['list', 'thread'] },
  // `mail.write` ON ALL THREE BELOW, which they lacked while they were inert:
  // reply-later writes an IMAP keyword and the bracket pair archives, so a
  // reader whose role cannot write mail must be told so rather than watch the
  // key do nothing. Gating is about telling the truth in the UI; the origin
  // re-checks every capability on every mutating route regardless.
  { id: 'replyLater', keys: 'h', tier: 'action', label: 'Reply later', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'archiveNext', keys: ']', tier: 'action', label: 'Archive and next', capability: 'mail.write', contexts: ['list', 'thread'] },
  { id: 'archivePrev', keys: '[', tier: 'action', label: 'Archive and previous', capability: 'mail.write', contexts: ['list', 'thread'] },
  // `list` AS WELL AS `thread`: the shell shows the message beside the list
  // rather than instead of it, so replying to the row under the cursor is a
  // list action too. Scoped to `thread` alone these three could never fire —
  // `useKeyboard` runs the shell in the `list` context — which was invisible
  // for as long as there was no composer for them to open.
  { id: 'reply', keys: 'r', tier: 'action', label: 'Reply', capability: 'mail.send', contexts: ['list', 'thread'] },
  // `a` STAYS THREAD-ONLY, unlike `r` and `f` above and below it. It is the one
  // of the three whose key is already spoken for — `* a` selects all and `g a`
  // navigates — and giving a bare `a` a list meaning as well would make the
  // most destructive of the three the one whose behaviour depends on where you
  // are. Reply-all from the list is a button.
  { id: 'replyAll', keys: 'a', tier: 'action', label: 'Reply all', capability: 'mail.send', contexts: ['thread'] },
  { id: 'forward', keys: 'f', tier: 'action', label: 'Forward', capability: 'mail.send', contexts: ['list', 'thread'] },
  { id: 'compose', keys: 'c', tier: 'action', label: 'Compose', capability: 'mail.send', contexts: ['global'] },
  { id: 'search', keys: '/', tier: 'action', label: 'Search', contexts: ['global'] },
  { id: 'undo', keys: 'z', tier: 'action', label: 'Undo', contexts: ['global'] },
  // ONE SURFACE, TWO NAMES, and that is a decision rather than an accident:
  // every command this client has is a bound command, so a runnable list of the
  // bindings IS the palette, and a separate cheat sheet would be the same list
  // rendered twice. `?` is the reference way in and `Mod+k` the runnable one;
  // both open `Help.tsx`. The day a command exists with no key, the palette
  // needs its own source and these part company.
  { id: 'palette', keys: 'Mod+k', tier: 'action', label: 'Command palette', contexts: ['global'] },
  { id: 'help', keys: '?', tier: 'action', label: 'Keyboard help', contexts: ['global'] },

  // Tier 2 - g-prefixed navigation.
  { id: 'go.inbox', keys: 'g i', tier: 'goto', label: 'Go to Inbox', contexts: ['global'] },
  { id: 'go.starred', keys: 'g s', tier: 'goto', label: 'Go to Starred', contexts: ['global'] },
  { id: 'go.sent', keys: 'g t', tier: 'goto', label: 'Go to Sent', contexts: ['global'] },
  { id: 'go.drafts', keys: 'g d', tier: 'goto', label: 'Go to Drafts', contexts: ['global'] },
  { id: 'go.all', keys: 'g a', tier: 'goto', label: 'Go to All mail', contexts: ['global'] },
  { id: 'go.snoozed', keys: 'g b', tier: 'goto', label: 'Go to Snoozed', contexts: ['global'] },
  { id: 'go.later', keys: 'g h', tier: 'goto', label: 'Go to Reply later', contexts: ['global'] },
  { id: 'go.rules', keys: 'g r', tier: 'goto', label: 'Go to Rules', capability: 'rules.gui', contexts: ['global'] },
  { id: 'go.settings', keys: 'g ,', tier: 'goto', label: 'Go to Settings', contexts: ['global'] },

  // Tier 3 - star-prefixed selection.
  { id: 'sel.all', keys: '* a', tier: 'select', label: 'Select all', contexts: ['list'] },
  { id: 'sel.none', keys: '* n', tier: 'select', label: 'Select none', contexts: ['list'] },
  { id: 'sel.read', keys: '* r', tier: 'select', label: 'Select read', contexts: ['list'] },
  { id: 'sel.unread', keys: '* u', tier: 'select', label: 'Select unread', contexts: ['list'] },
  { id: 'sel.starred', keys: '* s', tier: 'select', label: 'Select starred', contexts: ['list'] },

  // Deep mode. Present in the map but inert without the capability, so the help
  // sheet can show what exists and say why it is unavailable.
  { id: 'geek.inspector', keys: 'Mod+i', tier: 'action', label: 'Inspector', capability: 'geek.inspector', contexts: ['list', 'thread'] },
  { id: 'geek.console', keys: 'Mod+Shift+k', tier: 'action', label: 'Protocol console', capability: 'geek.console.read', contexts: ['global'] },
]

export interface KeyEvent {
  readonly key: string
  readonly ctrlKey?: boolean
  readonly metaKey?: boolean
  readonly shiftKey?: boolean
  readonly altKey?: boolean
}

/** Canonical name for a key event, in the same syntax as `Binding.keys`. */
export function describeKey(event: KeyEvent): string {
  const parts: string[] = []
  if (event.ctrlKey || event.metaKey) parts.push('Mod')
  if (event.altKey) parts.push('Alt')
  const key = event.key
  // Shift is only named when the character does not already imply it: "#" and
  // "!" are shifted keys, but naming them "Shift+#" would be noise.
  //
  // A SHIFTED LETTER ARRIVES UPPERCASE AND THE BINDINGS ARE WRITTEN LOWERCASE.
  // A browser reports `Shift+K` as `key: "K"`, so a rule that only lowercased
  // `[a-z]` produced `Mod+K` for a binding spelled `Mod+Shift+k` — and every
  // Shift binding in the map was therefore unreachable in a real browser while
  // matching perfectly in a test that synthesised `key: "k"`. `Shift+i` and
  // `Shift+u` are two more; `geek.console` is the one that found it.
  //
  // Lowercased rather than the binding being rewritten uppercase, because the
  // map is the thing people read and `Shift+I` reads as two shifts.
  if (event.shiftKey && /^[a-zA-Z]$/.test(key)) return [...parts, 'Shift', key.toLowerCase()].join('+')
  if (event.shiftKey && key.length > 1) return [...parts, 'Shift', key].join('+')
  return [...parts, key].join('+')
}

export interface Conflict {
  readonly keys: string
  readonly context: Context
  readonly ids: readonly string[]
}

/**
 * Two bindings conflict only if they share a key AND a context. `r` meaning
 * Reply inside a thread and `g r` meaning Go to Rules are not a conflict.
 */
export function findConflicts(bindings: readonly Binding[]): Conflict[] {
  const seen = new Map<string, Binding[]>()
  for (const binding of bindings) {
    for (const context of binding.contexts ?? ['global']) {
      const key = `${binding.keys}\u0000${context}`
      seen.set(key, [...(seen.get(key) ?? []), binding])
    }
  }
  const conflicts: Conflict[] = []
  for (const [key, group] of seen) {
    if (group.length < 2) continue
    const [keys, context] = key.split('\u0000') as [string, Context]
    conflicts.push({ keys, context, ids: group.map((b) => b.id) })
  }
  return conflicts
}

export type Resolution =
  | { readonly kind: 'pending'; readonly prefix: string }
  | { readonly kind: 'run'; readonly binding: Binding }
  | { readonly kind: 'blocked'; readonly binding: Binding; readonly capability: string }
  | { readonly kind: 'none' }

export interface ResolverOptions {
  readonly bindings?: readonly Binding[]
  /**
   * What the caller holds, from `/whoami`.
   *
   * ABSENT MEANS "NOT KNOWN YET" AND GATES NOTHING — it does NOT mean "holds
   * nothing". This defaulted to an empty set, which is the same value as a
   * genuinely capability-less principal, so the first `/whoami` that failed on
   * a flaky network would have made every gated key inert with a "you do not
   * have permission" notice, for an owner who has every permission there is.
   *
   * Gating here is about telling the truth in the UI. The origin re-checks
   * every capability on every mutating route, so an ungated key that should
   * have been gated is refused with a 403 rather than performed.
   */
  readonly capabilities?: ReadonlySet<string>
  /** User remapping: binding id to new key spec. */
  readonly overrides?: Readonly<Record<string, string>>
}

/**
 * The capability this binding needs and the reader has not got, or null.
 *
 * ONE PREDICATE, TWO READERS. `KeyResolver.gate` decides whether a keystroke
 * runs; the help sheet decides whether to draw a command as unavailable and say
 * why. They have to give the same answer, and the rule is subtle enough that a
 * second copy would get it wrong — `undefined`/`null` means "NOT KNOWN YET" and
 * gates NOTHING, which is not the same value as the empty set. Re-derived as
 * "empty set", the sheet would tell an owner who holds every permission there
 * is that they hold none, on first paint, before `/whoami` has answered.
 */
export function blockedCapability(
  binding: Binding,
  capabilities: ReadonlySet<string> | null | undefined,
): string | null {
  if (capabilities === null || capabilities === undefined) return null
  if (!binding.capability) return null
  return capabilities.has(binding.capability) ? null : binding.capability
}

/**
 * Where a person's remapping is stored.
 *
 * The registry in `packages/core/src/settings` owns this path; the shell checks
 * this string against `resolvePath` before reading the document, so a leaf
 * renamed there fails loudly at the call site rather than quietly reading
 * nothing here.
 */
export const KEY_OVERRIDES_PATH = '/keys/overrides'

export interface Overrides {
  /** Binding id to key spec — the direction `applyOverrides` takes. */
  readonly byId: Readonly<Record<string, string>>
  /** Chords the document remaps to a command this build does not have. */
  readonly ignored: readonly string[]
}

/**
 * The stored remapping, turned the way this file reads it.
 *
 * THE DOCUMENT AND THE RESOLVER SPELL THIS BACKWARDS FROM EACH OTHER, and this
 * function is the ONE place the two are reconciled. The registry declares
 * `/keys/overrides` as `{"chord": "command.id"}` — a keyboard layout, keyed by
 * the thing a person presses, which is also what makes "two commands on one
 * key" impossible to write down in the first place. `applyOverrides` below
 * wants the inverse, `{"command.id": "chord"}`, because it walks the bindings
 * and asks what each one was remapped to. Both spellings are right for their
 * own side; wiring them together without noticing produces a remap that
 * silently does nothing — which is a large part of why this pipeline sat here
 * fully tested and connected to nothing.
 *
 * AN ID THAT MATCHES NO BINDING IS DROPPED AND HANDED BACK. That is the trap
 * this file's header records — a command id matching no binding is a key that
 * does nothing, silently — and here the id comes from a document a person can
 * hand-edit, so the honest answer is to report the chord and let the help sheet
 * say it was ignored.
 *
 * TWO CHORDS FOR ONE COMMAND: the last one in the document wins, because a
 * binding holds one `keys` and there is no better answer than a definite one.
 * The help sheet shows which key a command ended up on, so the loser is visible
 * as an absence rather than as a mystery.
 */
export function overridesFromSettings(
  stored: unknown,
  bindings: readonly Binding[] = DEFAULT_BINDINGS,
): Overrides {
  const byId: Record<string, string> = {}
  const ignored: string[] = []
  // A tombstoned, missing or hand-broken leaf is not an error to report; it is
  // simply nobody's remapping. The shell must start with the default map.
  if (typeof stored !== 'object' || stored === null || Array.isArray(stored)) return { byId, ignored }
  const known = new Set(bindings.map((binding) => binding.id))
  for (const [chord, id] of Object.entries(stored as Record<string, unknown>)) {
    if (chord === '' || typeof id !== 'string' || id === '') continue
    if (!known.has(id)) {
      ignored.push(chord)
      continue
    }
    byId[id] = chord
  }
  return { byId, ignored }
}

/** Apply user remappings to the default map. */
export function applyOverrides(
  bindings: readonly Binding[],
  overrides: Readonly<Record<string, string>>,
): Binding[] {
  return bindings.map((b) => (overrides[b.id] ? { ...b, keys: overrides[b.id]! } : b))
}

/**
 * Chord-aware key resolver.
 *
 * Holds at most one pending prefix, and any key that does not complete a chord
 * clears it rather than being swallowed — so a mistyped chord never eats the
 * next command, which is the failure everyone hits with modal keymaps.
 */
export class KeyResolver {
  private prefix: string | null = null
  private readonly bindings: readonly Binding[]
  /** Null means not known yet. See `ResolverOptions.capabilities`. */
  private readonly capabilities: ReadonlySet<string> | null

  constructor(options: ResolverOptions = {}) {
    const base = options.bindings ?? DEFAULT_BINDINGS
    this.bindings = options.overrides ? applyOverrides(base, options.overrides) : base
    this.capabilities = options.capabilities ?? null
  }

  get pendingPrefix(): string | null {
    return this.prefix
  }

  reset(): void {
    this.prefix = null
  }

  /**
   * `context` is where focus is. In `compose` only modifier chords fire, so
   * typing an email never archives it.
   */
  resolve(event: KeyEvent, context: Context): Resolution {
    const name = describeKey(event)
    const isModified = name.startsWith('Mod')

    if (context === 'compose' && !isModified) {
      this.prefix = null
      return { kind: 'none' }
    }

    const candidates = this.bindings.filter((b) => {
      const contexts = b.contexts ?? ['global']
      return contexts.includes(context) || contexts.includes('global')
    })

    if (this.prefix !== null) {
      const chord = `${this.prefix} ${name}`
      this.prefix = null
      const match = candidates.find((b) => b.keys === chord)
      return match ? this.gate(match) : { kind: 'none' }
    }

    if (candidates.some((b) => b.keys.startsWith(name + ' '))) {
      this.prefix = name
      return { kind: 'pending', prefix: name }
    }

    const match = candidates.find((b) => b.keys === name)
    return match ? this.gate(match) : { kind: 'none' }
  }

  private gate(binding: Binding): Resolution {
    const missing = blockedCapability(binding, this.capabilities)
    return missing === null ? { kind: 'run', binding } : { kind: 'blocked', binding, capability: missing }
  }
}
